import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { validatedGet } from '../lib/api-client.ts';
import { UploadUiSpecSchema, type UploadUiSpec } from '@/types/casse/upload-ui-spec.ts';

interface UploadOutcome {
  ok: boolean;
  filename: string;
}

/**
 * wc-cassa-upload — Componente di upload casse parametrizzato dalla spec
 * server `GET /api/casse/<codice>/upload-ui-spec`.
 *
 * Comportamento:
 *  1. fetch della spec al mount (`afterRender`);
 *  2. validazione con `UploadUiSpecSchema`;
 *  3. se `spec.customComponent` e' presente E registrato → monta quel custom
 *     element come figlio passando la spec via `data-options`;
 *  4. altrimenti → render generico single-file (un dropzone su accept/maxFiles
 *     dichiarati dalla spec).
 *
 * Stato iniziale: spinner (`shared-loading-state`). Errori fetch: alert
 * (`shared-error-state`).
 *
 * Emette `cassa-upload-changed` con
 * `{ archivio: boolean, accessori: Record<string, UploadOutcome> }`.
 *
 * Attributi:
 *   codice  (obbligatorio) codice cassa (es. 'CE', 'EC').
 *   anno    (opzionale)    anno periodo, passato all'endpoint upload.
 *   mese    (opzionale)    mese periodo, passato all'endpoint upload.
 *   tipo    (opzionale)    tipo caricamento per casse multi-tipo (es. 'ISCRITTI'),
 *                          propagato al custom component e alla qs di upload.
 */
export class CassaUpload extends AppComponent {
  static override componentName = 'wc-cassa-upload';
  protected override get templateId() { return 'cassa-upload'; }

  private archivio: UploadOutcome = { ok: false, filename: '' };

  protected override afterRender(): void {
    void this.loadSpec();
  }

  private async loadSpec(): Promise<void> {
    const codice = this.attributeString('codice');
    if (!codice) {
      this.showError("Attributo 'codice' mancante");
      return;
    }

    const result = await validatedGet(`casse/${codice}/upload-ui-spec`, UploadUiSpecSchema);
    if (!result.ok) {
      this.showError(result.error);
      return;
    }

    this.mount(result.data);
  }

  private mount(spec: UploadUiSpec): void {
    const host = this.$('[data-host]');
    if (!host) return;
    host.innerHTML = '';

    if (spec.customComponent && customElements.get(spec.customComponent)) {
      this.mountCustom(host, spec);
      return;
    }

    if (spec.customComponent) {
      console.warn(
        `[wc-cassa-upload] customComponent "${spec.customComponent}" non registrato, fallback al rendering generico`,
      );
    }

    this.mountGenerico(host, spec);
  }

  private mountCustom(host: HTMLElement, spec: UploadUiSpec): void {
    const custom = document.createElement(spec.customComponent!);
    custom.setAttribute('codice', this.attributeString('codice'));
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');
    const tipo = this.attributeString('tipo');
    if (anno) custom.setAttribute('anno', anno);
    if (mese) custom.setAttribute('mese', mese);
    if (tipo) custom.setAttribute('tipo', tipo);
    custom.setAttribute('data-options', JSON.stringify(spec));

    custom.addEventListener('cassa-upload-changed', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      e.stopPropagation();
      this.dispatch('cassa-upload-changed', detail);
    });

    host.appendChild(custom);
  }

  private mountGenerico(host: HTMLElement, spec: UploadUiSpec): void {
    templateParseToElement('cassa-upload-generico', host, {
      helpText: spec.helpText ?? '',
    });

    const container = host.querySelector('[data-dropzone-container="archivio"]') as HTMLElement | null;
    if (!container) return;

    const codice = this.attributeString('codice');
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');
    const tipo = this.attributeString('tipo');
    const qs = new URLSearchParams({ cassa: codice, anno, mese, ruolo: 'archivio' });
    if (tipo) qs.set('tipo', tipo);
    const url = `/api/elaborazioni/upload?${qs.toString()}`;
    const isMulti = spec.multipleFiles;

    templateParseToElement('elaborazioni-dropzone', container, {
      url,
      accept: spec.acceptTypes.join(','),
      maxFiles: isMulti ? 10 : 1,
      send: isMulti ? 'queue' : 'auto',
      showUploadBtn: !isMulti,
    });

    const el = container.querySelector('[data-dropzone]') as HTMLElement | null;
    if (!el) return;

    el.addEventListener('uploaded.wx.dropzone', (e: Event) => {
      const detail = (e as CustomEvent).detail as { response?: { filename?: string } } | undefined;
      this.archivio = {
        ok: true,
        filename: detail?.response?.filename ?? '',
      };
      this.emitChange();
    });

    el.addEventListener('error.wx.dropzone', () => {
      this.archivio = { ok: false, filename: '' };
      this.emitChange();
    });

    el.addEventListener('complete.wx.dropzone', (e: Event) => {
      const detail = (e as CustomEvent).detail as { success?: number } | undefined;
      const success = detail?.success ?? 0;
      if (isMulti && success > 0) {
        this.archivio = { ok: true, filename: `${success} file caricati` };
        this.emitChange();
      }
    });
  }

  private emitChange(): void {
    this.dispatch('cassa-upload-changed', {
      archivio: this.archivio,
      accessori: {},
    });
  }

  private showError(message: string): void {
    const host = this.$('[data-host]');
    if (!host) return;
    templateParseToElement('shared-error-state', host, { message });
  }
}
